
import { useState, useEffect } from 'react';
import { supabase, type News } from '../../../lib/supabase';
import VideoPlayer from '../../../components/VideoPlayer';

const NewsManager = () => {
  const [news, setNews] = useState<News[]>([]);
  const [loading, setLoading] = useState(false);
  const [showNewsForm, setShowNewsForm] = useState(false);
  const [editingNews, setEditingNews] = useState<News | null>(null);

  const [newsForm, setNewsForm] = useState({
    title: '',
    text: '',
    image: '',
    video: '',
    category: 'Otros'
  });
  const [uploadingVideo, setUploadingVideo] = useState(false);

  const categories = [
    { value: 'Partidos', label: 'Partidos' },
    { value: 'Club', label: 'Club' },
    { value: 'Eventos', label: 'Eventos' },
    { value: 'Otros', label: 'Otros' }
  ];

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('news')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNews(data || []);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsForm.title.trim() || !newsForm.text.trim()) return;

    try {
      const newsData = {
        title: newsForm.title.trim(),
        text: newsForm.text.trim(),
        image: newsForm.image || null,
        category: newsForm.category,
        video_url: newsForm.video.trim() || null
      };

      if (editingNews) {
        const { error } = await supabase
          .from('news')
          .update(newsData)
          .eq('id', editingNews.id);
        
        if (error) {
          console.error('Error al actualizar noticia:', error);
          throw error;
        }
      } else {
        const { data, error } = await supabase
          .from('news')
          .insert([newsData])
          .select();
        
        if (error) {
          console.error('Error al insertar noticia:', error);
          throw error;
        }
        
        console.log('Noticia guardada exitosamente:', data);
      }

      setNewsForm({ title: '', text: '', image: '', video: '', category: 'Otros' });
      setShowNewsForm(false);
      setEditingNews(null);
      await fetchNews();
      
      // Actualizar página de noticias
      window.dispatchEvent(new CustomEvent('newsDataUpdated'));
    } catch (error) {
      console.error('Error completo al guardar noticia:', error);
      alert(`Error al guardar noticia: ${error instanceof Error ? error.message : 'Error desconocido'}`);
    }
  };

  const deleteNews = async (newsId: string) => {
    if (!confirm('¿Estás seguro de eliminar esta noticia?')) return;

    try {
      const { error } = await supabase
        .from('news')
        .delete()
        .eq('id', newsId);

      if (error) throw error;
      fetchNews();
      
      // Actualizar página de noticias
      window.dispatchEvent(new CustomEvent('newsDataUpdated'));
    } catch (error) {
      console.error('Error deleting news:', error);
      alert('Error al eliminar noticia');
    }
  };

  const editNews = (newsItem: News) => {
    setEditingNews(newsItem);
    setNewsForm({
      title: newsItem.title,
      text: newsItem.text,
      image: newsItem.image || '',
      video: newsItem.video_url || '',
      category: newsItem.category || 'Otros'
    });
    setShowNewsForm(true);
  };

  const openMediaSelector = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        // Validar tamaño del archivo (máximo 5MB)
        if (file.size > 5 * 1024 * 1024) {
          alert('El archivo es demasiado grande. Máximo 5MB.');
          return;
        }

        // Validar tipo de archivo
        if (!file.type.startsWith('image/')) {
          alert('Por favor selecciona un archivo de imagen válido.');
          return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
          const result = e.target?.result as string;
          setNewsForm({ ...newsForm, image: result });
        };
        reader.onerror = () => {
          alert('Error al leer el archivo. Por favor intenta de nuevo.');
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  const openVideoSelector = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/mp4,video/webm,video/ogg';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      // Validar tamaño (50MB máx)
      const maxSizeMB = 50;
      if (file.size > maxSizeMB * 1024 * 1024) {
        alert(`El archivo es demasiado grande. Máximo ${maxSizeMB}MB.`);
        return;
      }

      try {
        setUploadingVideo(true);
        const fileExt = file.name.split('.').pop();
        const fileName = `news-video-${Date.now()}.${fileExt}`;
        const filePath = `videos/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('media')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          console.error('Error uploading video:', uploadError);
          alert('Error al subir el video.');
          return;
        }

        const { data } = supabase.storage
          .from('media')
          .getPublicUrl(filePath);

        setNewsForm({ ...newsForm, video: data.publicUrl });
        alert('Video subido correctamente.');
      } catch (error) {
        console.error('Error uploading video:', error);
        alert('Error inesperado al subir el video.');
      } finally {
        setUploadingVideo(false);
      }
    };
    input.click();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Partidos':
        return 'bg-blue-500';
      case 'Club':
        return 'bg-green-500';
      case 'Eventos':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button
          onClick={() => {
            setEditingNews(null);
            setNewsForm({ title: '', text: '', image: '', video: '', category: 'Otros' });
            setShowNewsForm(true);
          }}
          className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
        >
          <i className="ri-add-line mr-2"></i>
          Añadir Noticia
        </button>
      </div>

      {loading ? (
        <p className="text-gray-400">Cargando noticias...</p>
      ) : news.length === 0 ? (
        <p className="text-gray-400">No hay noticias registradas</p>
      ) : (
        <div className="space-y-4">
          {news.map((newsItem) => (
            <div key={newsItem.id} className="bg-gray-700 border border-gray-600 rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-4 mb-3">
                    {newsItem.image && (
                      <img
                        src={newsItem.image}
                        alt={newsItem.title}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-white">{newsItem.title}</h3>
                        <span className={`px-2 py-1 text-xs font-medium text-white rounded-full ${getCategoryColor(newsItem.category)}`}>
                          {newsItem.category}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-300">
                        <span>
                          <i className="ri-calendar-line mr-1"></i>
                          {formatDate(newsItem.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm line-clamp-3">
                    {newsItem.text}
                  </p>
                  {newsItem.video_url && (
                    <div className="flex items-center gap-2 text-blue-300 text-sm mt-3">
                      <i className="ri-play-circle-line"></i>
                      <span>Video adjunto</span>
                    </div>
                  )}
                </div>
                <div className="flex space-x-2 ml-4">
                  <button
                    onClick={() => editNews(newsItem)}
                    className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                  >
                    <i className="ri-edit-line"></i>
                  </button>
                  <button
                    onClick={() => deleteNews(newsItem.id)}
                    className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Noticia */}
      {showNewsForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4 text-white">
              {editingNews ? 'Editar Noticia' : 'Añadir Noticia'}
            </h3>
            <form onSubmit={handleNewsSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Título *
                </label>
                <input
                  type="text"
                  value={newsForm.title}
                  onChange={(e) => setNewsForm({ ...newsForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Categoría *
                </label>
                <select
                  value={newsForm.category}
                  onChange={(e) => setNewsForm({ ...newsForm, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  required
                >
                  {categories.map((category) => (
                    <option key={category.value} value={category.value}>
                      {category.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Contenido *
                </label>
                <textarea
                  value={newsForm.text}
                  onChange={(e) => setNewsForm({ ...newsForm, text: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  rows={6}
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Imagen de la Noticia
                </label>
                <button
                  type="button"
                  onClick={openMediaSelector}
                  className="w-full px-4 py-2 bg-gray-700 text-gray-300 rounded-md hover:bg-gray-600 border-2 border-dashed border-gray-600 transition-colors"
                >
                  <i className="ri-upload-line mr-2"></i>
                  {newsForm.image ? 'Cambiar Imagen' : 'Seleccionar desde Medios'}
                </button>
                {newsForm.image && (
                  <div className="mt-3">
                    <p className="text-sm text-gray-300 mb-2">Vista previa:</p>
                    <img
                      src={newsForm.image}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-lg border border-gray-600"
                    />
                    <button
                      type="button"
                      onClick={() => setNewsForm({ ...newsForm, image: '' })}
                      className="mt-2 text-sm text-red-400 hover:text-red-300"
                    >
                      <i className="ri-delete-bin-line mr-1"></i>
                      Eliminar imagen
                    </button>
                  </div>
                )}
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Video (URL opcional)
                </label>
                <div className="flex flex-col gap-2 mb-2">
                  <button
                    type="button"
                    onClick={openVideoSelector}
                    disabled={uploadingVideo}
                    className="px-4 py-2 bg-gray-700 text-gray-200 rounded-md hover:bg-gray-600 border-2 border-dashed border-gray-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <i className="ri-upload-cloud-2-line"></i>
                    {uploadingVideo ? 'Subiendo video...' : newsForm.video ? 'Cambiar Video' : 'Subir Video desde Medios'}
                  </button>
                  <span className="text-xs text-gray-400">
                    Formatos soportados: MP4, WebM, OGG. Tamaño máximo 50MB.
                  </span>
                </div>
                <input
                  type="url"
                  value={newsForm.video}
                  onChange={(e) => setNewsForm({ ...newsForm, video: e.target.value })}
                  placeholder="https://youtu.be/..., https://vimeo.com/... o https://tusitio.com/video.mp4"
                  className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <p className="text-xs text-gray-400 mt-1">
                  También puedes pegar enlaces de YouTube, Vimeo o archivos de vídeo (MP4, WEBM, OGG).
                </p>
                {newsForm.video && (
                  <div className="mt-3 space-y-2">
                    <p className="text-sm text-gray-300">Vista previa del video:</p>
                    <VideoPlayer url={newsForm.video} />
                    <button
                      type="button"
                      onClick={() => setNewsForm({ ...newsForm, video: '' })}
                      className="text-sm text-red-400 hover:text-red-300"
                    >
                      <i className="ri-delete-bin-line mr-1"></i>
                      Eliminar video
                    </button>
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowNewsForm(false);
                    setEditingNews(null);
                    setNewsForm({ title: '', text: '', image: '', video: '', category: 'Otros' });
                  }}
                  className="px-4 py-2 text-gray-300 border border-gray-600 rounded-md hover:bg-gray-700"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={!newsForm.title.trim() || !newsForm.text.trim()}
                  className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:bg-gray-600 disabled:cursor-not-allowed"
                >
                  {editingNews ? 'Actualizar' : 'Añadir'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsManager;
